import {
  Typography,
  FontSizeEnum,
  FontWeightEnum,
  FontColorEnum,
  IconButtonSection,
  IconSizeEnum,
  PlusIcon,
  ModalDialog,
  ModalMessageColorsEnum,
  ModalMessage,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TableWithDetailCard } from '@/components/TableWithDetailCard/TableWithDetailCard';
import { FormTypeEnum } from '@/types';
import { getParamsFromTableFilters } from '@/utils/table-filters';

import { useChannelRecordings } from '../api/get-channel-recordings';
import { usePatchChannelRecording } from '../api/patch-channel-recording';
import { usePostChannelRecording } from '../api/post-channel-recordings';
import { ChannelRecordingDetailCard } from '../components/ChannelRecordingDetailCard/ChannelRecordingDetailCard';
import { ChannelRecordingsDrawerForm } from '../components/ChannelRecordingsDrawerForm/ChannelRecordingsDrawerForm';
import { ChannelRecordingsTable } from '../components/ChannelRecordingsTable/ChannelRecordingsTable';
import { useChannelRecordingSchedulesPermissions } from '../hooks/useChannelRecordingSchedulesPermissions';
import { useChannelRecordingsController } from '../hooks/useChannelRecordingsController';
import { useChannelRecordingFilters } from '../stores/channel-recording-filters';
import { ChannelRecordingType } from '../types';

export default function ChannelRecordingsPage() {
  const { t } = useTranslation();
  const { filters, paginationValues, clearFilters } = useChannelRecordingFilters();

  /**
   * Resets table filters on page unmount.
   */
  useEffect(() => {
    return () => {
      clearFilters();
    };
  }, [clearFilters]);

  const {
    data: { data: items = [], total = 0 } = {},
    isFetching,
    isError,
  } = useChannelRecordings({
    params: getParamsFromTableFilters({
      filters,
      paginationValues,
    }),
  });

  const { channelRecordingDialog, channelRecordingDrawer, handleDeleteChannelRecording } =
    useChannelRecordingsController();
  const { mutate: createChannelRecording, isPending: isCreatingChannelRecording } =
    usePostChannelRecording({
      config: {
        onSuccess: () => {
          channelRecordingDrawer.close();
        },
      },
    });
  const { mutate: updateChannelRecording, isPending: isUpdatingChannelRecording } =
    usePatchChannelRecording({
      config: {
        onSuccess: () => {
          channelRecordingDrawer.close();
        },
      },
    });

  const { canCreate, canUpdate, canDuplicate, canDelete } =
    useChannelRecordingSchedulesPermissions();

  const onCreate = () => {
    if (!canCreate) return;
    channelRecordingDrawer.open({ type: FormTypeEnum.CREATE, data: null });
  };

  const onUpdate = (channelRecording: ChannelRecordingType) => {
    if (!canUpdate) return;
    channelRecordingDrawer.open({ type: FormTypeEnum.UPDATE, data: channelRecording });
  };

  const onDuplicate = (channelRecording: ChannelRecordingType) => {
    if (!canDuplicate) return;
    channelRecordingDrawer.open({ type: FormTypeEnum.DUPLICATE, data: channelRecording });
  };

  const onDelete = (channelRecording: ChannelRecordingType) => {
    if (!canDelete) return;
    channelRecordingDialog.open(channelRecording);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Programación de grabaciones')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              className="mr-size-16"
              onClick={onCreate}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: PlusIcon,
                },
                text: {
                  value: t('Nueva'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <TableWithDetailCard<ChannelRecordingType>
          isError={isError}
          renderTable={({ handleOpenSecondCard, data }) => (
            <ChannelRecordingsTable
              items={items}
              totalItems={total}
              isFetching={isFetching}
              activeItem={data}
              onUpdate={canUpdate ? onUpdate : undefined}
              onDuplicate={canDuplicate ? onDuplicate : undefined}
              onDelete={canDelete ? onDelete : undefined}
              onSelect={handleOpenSecondCard}
            />
          )}
          renderDetailCard={({ data, isSecondCardCollapsed, handleSecondCardCollapse }) => (
            <ChannelRecordingDetailCard
              channelRecording={data}
              isCollapsed={isSecondCardCollapsed}
              onCollapse={handleSecondCardCollapse}
              onUpdate={onUpdate}
            />
          )}
        />
      </div>

      <ChannelRecordingsDrawerForm
        data={channelRecordingDrawer.data}
        isOpen={channelRecordingDrawer.isOpen}
        onClose={channelRecordingDrawer.close}
        onSubmit={data => {
          if (channelRecordingDrawer.data?.type === FormTypeEnum.UPDATE) {
            updateChannelRecording({
              ...data,
              production_centers: [data.production_center_id],
              id: channelRecordingDrawer.data.data.id,
            });
          } else {
            createChannelRecording({
              ...data,
              production_centers: [data.production_center_id],
            });
          }
        }}
        isMutating={isCreatingChannelRecording || isUpdatingChannelRecording}
      />

      <ModalDialog
        isOpen={channelRecordingDialog.isOpen}
        onClose={channelRecordingDialog.close}
        onAction={handleDeleteChannelRecording}
        heading={t('Eliminar programación de grabación')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage
          title={t(
            '¿Estás seguro de querer eliminar la programación de la grabación seleccionada?'
          )}
        />
      </ModalDialog>
    </>
  );
}
